import { Dropdown, Input, Spin } from 'antd';
import { useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import React, { useMemo, useRef, useState } from 'react';
import { ApprovalWorkflowItemDto, AWAssetDto, GetAWSuccessDto } from '@api/Api';
import { useDebounced } from '@helpers/useDebounced';
import { useGetApprovalWorkflow } from '@hooks/queries/approval';
import LottieComponent from '@components/Lottie';
import ApprovalAssetCard from '@components/Approval/ApprovalAssetsList/components/ApprovalAssetCard';
import ApprovalBulkDecision from '@components/Approval/ApprovalBulkActions/ApprovalBulkDecision';
import { sortedAWListAssetsToReviewInBatch } from '@components/Modals/ApprovalModal/utils';
import './ApprovalAssetsList.less';
import { useWebSocketDebouncedSubscription } from '@hooks/useWebSocketClient';
import { ArrowDownIcon, SearchIcon, SortArrowIcon } from '@icons';

type ApprovalAssetsListProps = {
  initialData?: GetAWSuccessDto;
  enabled?: boolean;
  password?: string | null;
  awId: string | null;
  onOpenAsset: (value: {
    approval: ApprovalWorkflowItemDto;
    asset: AWAssetDto;
  }) => void;
};

export default function ApprovalAssetsList({
  initialData,
  awId,
  enabled = true,
  password,
  onOpenAsset
}: ApprovalAssetsListProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const timeoutId = useRef<ReturnType<typeof setTimeout>>();
  const [selectedAssets, setSelectedAssets] = useState<AWAssetDto[]>([]);
  const sortParam = searchParams.get('sortBy') || 'needReview';
  const statusParam = searchParams.get('status') || 'allAssets';
  const searchParam = searchParams.get('search') || undefined;
  const searchText = useDebounced(searchParam, 600);

  const {
    data: approvalWorkflow,
    isLoading: isLoadingApprovalWorkflow,
    refetch
  } = useGetApprovalWorkflow({
    initialData,
    enabled,
    params: {
      id: awId || '',
      password
    }
  });

  const aw = approvalWorkflow?.approvalWorkflow;

  const currentStage = useMemo(() => {
    if (!aw) return null;
    if (!aw.stages || !aw.stages.length) return null;
    return (
      aw.stages.find((stage) => stage.id === aw.currentStageId) || aw.stages[0]
    );
  }, [aw?.stages, aw?.currentStageId]);

  const currentMember = useMemo(
    () => currentStage?.members.find((member) => member.me),
    [currentStage?.members]
  );

  const toReviewCount = useMemo(() => {
    if (!currentMember?.canSubmitDecision) {
      return 0;
    }
    return aw?.assets.length || 0;
  }, [aw?.assets.length, currentMember?.canSubmitDecision]);

  const reviewCount = useMemo(() => {
    if (!currentMember?.canSubmitDecision) {
      return 0;
    }
    return (
      aw?.assets.filter((asset) =>
        currentMember?.decisions?.some(
          (x) => x.assetVersionId === asset.versionId && x.decision
        )
      ).length || 0
    );
  }, [aw?.assets, currentMember?.canSubmitDecision, currentMember?.decisions]);

  const canReview = toReviewCount > 0;

  const sortedApprovalListAssets = useMemo(() => {
    if (!aw?.assets?.length) return [];
    let _approvalListAssets = aw?.assets;
    if (searchText) {
      _approvalListAssets = _approvalListAssets.filter((item) => {
        return item.name
          .toLocaleLowerCase()
          .includes(searchText.toLocaleLowerCase());
      });
    }
    if (statusParam !== 'allAssets' && !!statusParam) {
      _approvalListAssets = _approvalListAssets.filter((item) => {
        if (statusParam === 'waiting_for_review') return !item.decision;
        return item.decision === statusParam;
      });
    }
    return sortedAWListAssetsToReviewInBatch(_approvalListAssets, sortParam);
  }, [aw?.assets, statusParam, searchText, sortParam]);

  const onChangeSelectedAssets = (asset: AWAssetDto) => {
    setSelectedAssets((prevSelectedAssets) => {
      const existAsset = prevSelectedAssets.find(
        (item) => item.id === asset.id
      );
      if (existAsset)
        return prevSelectedAssets.filter((item) => item.id !== asset.id);
      return [...prevSelectedAssets, asset];
    });
  };

  const assetsFiltersItems = useMemo(() => {
    return [
      { label: 'All assets', key: 'allAssets' },
      {
        label: (
          <>
            <span
              className="help_dropdown__dot"
              style={{ background: '#F5DAE8' }}
            />{' '}
            Waiting for review
          </>
        ),
        key: 'waiting_for_review'
      },
      {
        label: (
          <>
            <span
              className="help_dropdown__dot"
              style={{ background: '#92D1BA' }}
            />{' '}
            Approved
          </>
        ),
        key: 'approved'
      },
      {
        label: (
          <>
            <span
              className="help_dropdown__dot"
              style={{ background: '#BFC5F4' }}
            />{' '}
            Approved with feedback
          </>
        ),
        key: 'approved_with_feedback'
      },
      {
        label: (
          <>
            <span
              className="help_dropdown__dot"
              style={{ background: '#F7CAB0' }}
            />{' '}
            Changes required
          </>
        ),
        key: 'changes_required'
      }
    ];
  }, []);

  const selectedFilterLabel = useMemo(() => {
    if (statusParam === 'allAssets') return 'Status';
    return (
      assetsFiltersItems.find((item) => item.key === statusParam)?.label ||
      'All assets'
    );
  }, [statusParam]);

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.id,
    () => refetch(),
    1000,
    (msg) => {
      if (msg.payload.id !== awId) return false;
      return !msg.causedByMe;
    },
    'aw-updated',
    'aw-removed'
  );

  if (isLoadingApprovalWorkflow)
    return (
      <div
        className="approval-assets-list"
        style={{
          height: 'calc(100dvh - 300px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Spin size="large" />
      </div>
    );

  return (
    <div className="approval-assets-list">
      <div className="approval-assets-list_title">
        {aw?.name || 'Media list'}&nbsp;&nbsp;
        {canReview && (
          <span className="approval-assets-list_count">
            {reviewCount}/{toReviewCount}
          </span>
        )}
      </div>
      <div className="approval-assets-list_head">
        <div className="approval-assets-list_actions">
          <Dropdown
            menu={{
              selectable: true,
              selectedKeys: [statusParam],
              onClick: ({ key }: { key: string }) => {
                searchParams.set('status', key);
                setSearchParams(searchParams);
              },
              items: assetsFiltersItems
            }}
            prefixCls="approval-assets-list_dropdown"
            overlayClassName="media_viewer_mobile-overlay"
            placement="bottomLeft"
            trigger={['click']}
          >
            <button
              type="button"
              className={classNames('approval-assets-list_dropdown-btn', {
                'b-selected': statusParam !== 'allAssets'
              })}
            >
              {selectedFilterLabel}
              <ArrowDownIcon />
            </button>
          </Dropdown>
        </div>
        <div className="approval-assets-list_actions">
          <Input
            style={{ width: '100%', marginRight: 16, padding: '8px 12px' }}
            placeholder="Search"
            prefix={<SearchIcon />}
            size="large"
            defaultValue={searchParam || ''}
            onChange={(e) => {
              const _searchText = e.target.value.trim();
              if (timeoutId.current) clearTimeout(timeoutId.current);
              timeoutId.current = setTimeout(() => {
                if (_searchText) searchParams.set('search', _searchText);
                if (!_searchText) searchParams.delete('search');
                setSearchParams(searchParams);
              }, 400);
            }}
          />
          <Dropdown
            menu={{
              selectable: true,
              selectedKeys: [sortParam],
              onClick: ({ key }: { key: string }) => {
                searchParams.set('sortBy', key);
                setSearchParams(searchParams);
              },
              items: [
                { label: 'Need to review', key: 'needReview' },
                { label: 'Already completed review', key: 'completedReview' }
              ]
            }}
            prefixCls="approval-assets-list_dropdown"
            placement="bottomRight"
            trigger={['click']}
          >
            <button
              type="button"
              className="approval-assets-list_dropdown-btn-white"
            >
              <SortArrowIcon />
              Sort
            </button>
          </Dropdown>
        </div>
      </div>
      {!sortedApprovalListAssets.length && (
        <div className="approval-assets-list_empty">
          <LottieComponent
            className="approval-assets-list_empty-icon"
            view="addMediaInFolder"
          />
          <strong className="main-body-text main-body-text--bold">
            No assets found
          </strong>
          <p className="main-body-text">try selecting other options</p>
        </div>
      )}
      {!!sortedApprovalListAssets.length && aw && (
        <div className="approval-assets-list_content">
          {(sortedApprovalListAssets || []).map((item) => {
            const isSelected = !!selectedAssets.find(
              (asset) => asset.id === item.id
            );
            return (
              <div key={item.id} className="approval-assets-list_item">
                <ApprovalAssetCard
                  asset={item}
                  approvalWorkflow={aw}
                  isSelected={isSelected}
                  onOpenAsset={onOpenAsset}
                  onSelect={onChangeSelectedAssets}
                />
              </div>
            );
          })}
          <ApprovalBulkDecision
            approvalWorkflowId={aw?.id || ''}
            currentStageId={aw?.currentStageId || ''}
            assets={selectedAssets}
            password={password || null}
            onClose={() => setSelectedAssets([])}
          />
        </div>
      )}
    </div>
  );
}
